import { Signal } from "signals";
import Components from "slotworks/components/components";
import ReelComponent from "slotworks/components/matrix/reel/reel-component";
import AbstractReelTransition from "slotworks/components/matrix/reel/transition-behaviours/abstract-reel-transition";
import ReelSpinner from "slotworks/components/matrix/reel/transition-behaviours/spin/reel-spinner";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import { Services } from "slotworks/services/services";
import { SoundEvent } from "slotworks/services/sound/sound-events";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel"; import Sequence from "slotworks/utils/contracts/sequence";
import { AnticipationResult } from "slotworks/model/gameplay/records/results/anticipation-result";

export default class SpinReelTransition extends AbstractReelTransition {

    public static allowSkips: boolean = true;
    public static allowAnticipationSkips: boolean = true;

    public onReelLand: Signal = new Signal();

    private spinners: ReelSpinner[];

    private startGap: number = 100;
    private stopGap: number = 100;
    private quickStopGap: number = 50;

    private allowPartialSkips: boolean = true;

    private anticipationResult: AnticipationResult;
    private anticipatingSymbols: SymbolComponent[];

    private anticipatingReels: number[] = [];

    private spinningReels: number;
    private skipping: boolean;
    private skipPossible: boolean;

    private heldReels: number[] = [];

    // Race handler function, see checkRaceComplete
    private completeResolve: Function;

    constructor(startGap: number, stopGap: number, quickStopGap: number, allowPartialSkips: boolean) {
        super();

        this.startGap = startGap;
        this.stopGap = stopGap;
        this.quickStopGap = quickStopGap;
        this.allowPartialSkips = allowPartialSkips;
    }

    public init(reels: ReelComponent[], stops: number[], reelset: string[][]) {
        super.init(reels, stops, reelset);

        this.spinners = [];

        for (let i = 0; i < reels.length; i++) {
            const reel = reels[i];
            const spinner = this.createReelSpinner(reel, stops[i], reelset[i]);
            this.spinners.push(spinner);
            spinner.landSignal.add((index: number) => this.reelLandedHandler(index));
            spinner.onAnticipationStart.add((index: number) => this.anticipatingStartHandler(index));
            spinner.onAnticipationEnd.add((index: number) => this.anticipatingEndHandler(index));
        }
    }

    protected createReelSpinner(reel: ReelComponent, reelstop: number, reelstrip: string[]): ReelSpinner {
        return new ReelSpinner(reel, reelstop, reelstrip);
    }

    public start(reelset?: string[][], jumpStart: boolean = false, quickSpin: boolean = false): Contract<void> {
        this.anticipatingReels = [];
        this.skipPossible = true;
        this.skipping = false;

        Services.sound.event(SoundEvent.reel_transition_start);

        Components.matrix.resetSymbols();

        const startMethods = [];

        for (let spinnerIndex = 0; spinnerIndex < this.spinners.length; spinnerIndex++) {
            if (this.heldReels.indexOf(spinnerIndex) === -1) {
                const spinner = this.spinners[spinnerIndex];
                startMethods.push(() => spinner.start(jumpStart ? 0 : this.startGap * spinnerIndex, jumpStart, quickSpin));
            }
        }

        this.spinningReels = startMethods.length;

        return new Parallel(startMethods);
    }

    public stop(spinRecord: SpinRecord, quickSpin: boolean = false): Contract<void> {
        this.anticipatingSymbols = [];

        const anticipatations: AnticipationResult[] = spinRecord.getResultsOfType(AnticipationResult);
        this.anticipationResult = anticipatations[0];

        const anticipations = this.anticipationResult.reelAnticipations;

        const stopMethods: Array<() => Contract<void>> = [];

        let anticipating = false;

        if (anticipations) {
            for (const anticipate of anticipations) {
                if (anticipate) {
                    anticipating = true;
                }
            }
        }

        if (quickSpin) {
            anticipating = false;
        }

        let stopGap = 0;
        let wasAnticipating = false;

        for (let spinnerIndex = 0; spinnerIndex < this.spinners.length; spinnerIndex++) {

            if (this.heldReels.indexOf(spinnerIndex) !== -1) {
                continue;
            }

            const spinner = this.spinners[spinnerIndex];

            if (!quickSpin && anticipations && anticipations[spinnerIndex]) {
                wasAnticipating = true;
                stopMethods.push(() => spinner.anticipate(spinRecord.stops[spinnerIndex], spinRecord.reelset[spinnerIndex]));
            } else {
                // Reset stop gap after an anticipation since stops will be queued to wait for anticipation reels to complete
                if (wasAnticipating) {
                    wasAnticipating = false;
                    stopGap = 0;
                }
                const reelStopGap = stopGap;
                stopMethods.push(() => spinner.stop(spinRecord.stops[spinnerIndex], spinRecord.reelset[spinnerIndex], reelStopGap, quickSpin));
            }

            stopGap += (quickSpin ? this.quickStopGap : this.stopGap);
        }

        if (!anticipating) {
            // If not anticipating, all reels are stopped at the same time (the delay between reels is internal)
            return new Contract<void>((resolve) => {
                new Parallel(stopMethods).then(() => {
                    Services.sound.event(SoundEvent.reel_transition_end);
                    this.checkRaceComplete(() => resolve(null));
                });
            });
        } else {
            // If anticipation is present, subsequent reels are delayed until the anticipation is finished (since the gap is unpredictable),
            // and then are stopped in parallel until the next anticipation reel and so on
            return new Contract<void>((resolve) => {

                let anticipationStarted = false;

                const getNextStop = (stop: number, parallels: Array<() => Contract<void>>): Contract<any> => {
                    if (stopMethods.length > stop) {
                        if (!anticipations[stop]) {
                            parallels.push(stopMethods[stop]);
                            return getNextStop(stop + 1, parallels);
                        } else {
                            return new Parallel(parallels).then(() => {
                                if (!anticipationStarted) {
                                    Services.sound.event(SoundEvent.reel_transition_anticipate);
                                    anticipationStarted = true;
                                }
                                stopMethods[stop]().then(() => getNextStop(stop + 1, []));
                            });
                        }
                    } else {
                        if (parallels.length) {
                            new Parallel(parallels).then(() => {
                                Services.sound.event(SoundEvent.reel_transition_end);
                                this.checkRaceComplete(() => resolve(null));
                            });
                        } else {
                            Services.sound.event(SoundEvent.reel_transition_end);
                            this.checkRaceComplete(() => resolve(null));
                        }
                    }
                };

                getNextStop(0, []);
            });
        }
    }

    public skip(spinRecord: SpinRecord): Contract<void> {
        if (!SpinReelTransition.allowSkips) {
            return new Contract<void>((resolve) => null);
        }

        this.skipping = true;
        this.skipPossible = false;
        let alreadyStopping = false;

        for (const spinner of this.spinners) {
            if (spinner.isStopping()) {
                alreadyStopping = true;
                break;
            }
        }

        if (!alreadyStopping || this.allowPartialSkips) {
            Services.sound.event(SoundEvent.reel_transition_skip);

            // Begin by pushing them all in a single parallel, but whenever ananticipation is found, queue it up, then create a new parallel for subsequent reels
            let parallelQueue: Array<() => Contract<void>> = [];
            const sequenceQueue: Array<() => Contract<void>> = [];

            // For each reel
            for (let i = 0; i < this.spinners.length; i++) {
                // Check if the reel should anticipate
                if (SpinReelTransition.allowAnticipationSkips && this.anticipationResult.reelAnticipations[i]) {
                    // Push current parallel queue to sequence and create a new parallel queue to run after the anticipation
                    if (parallelQueue.length) {
                        const pushQueue = [...parallelQueue];
                        sequenceQueue.push(() => new Parallel(pushQueue));
                    }
                    parallelQueue = [];

                    // Add anticipatation reel in sequence
                    if (!this.spinners[i].anticipating && !this.spinners[i].wasAnticipating) {
                        // Because an anticipation will be triggered, it's possible to skip again to skip the anticipation
                        this.skipPossible = true;
                        sequenceQueue.push(() => {
                            return this.spinners[i].anticipate(spinRecord.stops[i], spinRecord.reelset[i]);
                        });
                    } else {
                        /// ANTIC END SIGNAL?
                        sequenceQueue.push(() => {
                            return this.spinners[i].skip(spinRecord.stops[i], spinRecord.reelset[i]);
                        });
                    }

                } else {
                    // Add skips to current parallel queue
                    parallelQueue.push(() => {
                        return this.spinners[i].skip(spinRecord.stops[i], spinRecord.reelset[i]);
                    });
                }
            }

            // Push any remaining parallels to queue
            if (parallelQueue.length) {
                sequenceQueue.push(() => new Parallel([...parallelQueue]));
            }

            return new Contract<void>((resolve) => {
                new Sequence(sequenceQueue).then(() => {
                    Services.sound.event(SoundEvent.reel_transition_skip_end);
                    this.checkRaceComplete(() => resolve(null));
                });
            });
        } else {
            return new Contract<void>((resolve) => null);
        }
    }

    public canSkip() {
        return this.skipPossible;
    }

    public jump(stops: number[], reelset: string[][]) {
        for (let i = 0; i < this.spinners.length; i++) {

            const spinner = this.spinners[i];

            spinner.jump(stops[i], reelset[i]);
        }
    }

    public reelLandedHandler(reelIndex: number) {
        this.onReelLand.dispatch(reelIndex);

        const lands = [];
        const landedReel = Components.matrix.getReels()[reelIndex - 1];
        const reelSymbols = landedReel.getSymbols();
        const validLandSymbols = this.anticipationResult.landSymbols[reelIndex - 1];
        const hasAnticipation = (this.anticipationResult && this.anticipationResult.anticipateSymbols.length > reelIndex);
        const symbolsToAnimate = hasAnticipation ? this.anticipationResult.anticipateSymbols[reelIndex] : [];
        for (const symbol of reelSymbols) {
            if (!validLandSymbols || validLandSymbols.indexOf(symbol.symbolId) > -1) {
                lands.push(() => symbol.land());
            }
        }

        new Parallel(lands).then(() => {
            this.spinningReels--;
            this.checkRaceComplete();

            if (!this.skipping) {
                if (symbolsToAnimate.length) {
                    let symbols: SymbolComponent[] = [];
                    const reels = Components.matrix.getReels().slice(0, reelIndex);
                    for (const reel of reels) {
                        if (reel.index <= reelIndex) {
                            symbols = symbols.concat(reel.getSymbols());
                        }
                    }
                    for (const symbol of symbols) {
                        if (symbolsToAnimate.indexOf(symbol.symbolId) > -1) {
                            if (this.anticipatingSymbols.indexOf(symbol) === -1) {
                                symbol.anticipate().execute();
                                this.anticipatingSymbols.push(symbol);
                            }
                        }
                    }
                }
            }
        });
    }

    public hold(reelIndexes?: number[]) {
        if (!reelIndexes) {
            reelIndexes = [];
        }
        this.heldReels = reelIndexes;
    }

    public update() {
        super.update();

        for (const spinner of this.spinners) {
            spinner.update();
        }
    }

    public getSpinners() {
        return this.spinners;
    }

    private stopAnticipatingSymbols() {
        for (const symbol of this.anticipatingSymbols) {
            symbol.static();
        }

        this.anticipatingSymbols = [];
    }

    // Resolves race condition between symbol land animation and reels stopping
    private checkRaceComplete(resolve?: Function) {
        if (resolve) {
            this.completeResolve = resolve;
        }

        if (this.spinningReels === 0) {
            if (this.completeResolve) {
                this.stopAnticipatingSymbols();
                this.completeResolve();
                this.completeResolve = null;
            }
        }
    }

    private anticipatingStartHandler(index: number) {
        if (this.anticipatingReels.length === 0) {
            this.onAnticipationStart.dispatch();
        }

        this.anticipatingReels.push(index);
    }

    private anticipatingEndHandler(index: number) {
        this.anticipatingReels.pop();

        if (this.anticipatingReels.length === 0) {
            this.onAnticipationEnd.dispatch();
        }
    }
}
